import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { documentsApi } from '@/lib/documents';
import { AppShell } from '@/components/app/AppShell';
import { CommentThread } from '@/components/CommentThread';
import { AskAboutButton } from '@/components/app/AskAboutButton';
import { EmptyState } from '@/components/app/EmptyState';
import { JpDate } from '@/components/jp/JpDate';

export function DocViewerPage() {
  const { t } = useTranslation();
  const { wsId, docId } = useParams<{ wsId: string; docId: string }>();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['document', wsId, docId],
    queryFn: () => documentsApi.get(wsId!, docId!),
    enabled: !!wsId && !!docId,
  });
  const similar = useQuery({
    queryKey: ['similar', wsId, docId],
    queryFn: () => documentsApi.findSimilar(wsId!, docId!),
    enabled: !!wsId && !!docId,
  });
  const reindex = useMutation({
    mutationFn: () => documentsApi.reindex(wsId!, docId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['document', wsId, docId] }),
  });

  return (
    <AppShell
      title={data?.title || t('common.loading', { defaultValue: 'Loading…' })}
      description={t('docviewer.subtitle', { defaultValue: 'Document metadata and ingestion provenance.' }) as string}
      maxWidth="max-w-4xl"
      actions={
        <div className="flex items-center gap-3">
          <Link to={`/app/workspace/${wsId}/docs`} className="text-[13px] text-ink-500 hover:text-accent-600">
            ← {t('docviewer.back', { defaultValue: 'Documents' })}
          </Link>
          {data?.status === 'failed' && (
            <button
              onClick={() => reindex.mutate()}
              disabled={reindex.isPending}
              className="text-[13px] text-accent-600 hover:underline disabled:opacity-40"
            >
              {reindex.isPending ? t('docs.reindexing', { defaultValue: 'Re-indexing…' }) : t('docviewer.retry', { defaultValue: 'Retry indexing' })}
            </button>
          )}
          {data && (
            <Link to={`/app/workspace/${wsId}/search?seed=${encodeURIComponent(data.title)}`} className="text-[13px] text-accent-600 hover:underline">
              {t('docviewer.search_inside', { defaultValue: 'Search inside' })}
            </Link>
          )}
          {data && <AskAboutButton wsId={wsId!} seed={data.title} className="btn-press px-3.5 py-1.5 text-[13px]" />}
        </div>
      }
    >
      <div className="card-paper p-6">
        {isLoading && <p className="text-sm text-ink-500">{t('common.loading', { defaultValue: 'Loading…' })}</p>}
        {data && (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <dt className="text-ink-500">{t('docviewer.id', { defaultValue: 'ID' })}</dt>
            <dd className="font-mono text-xs text-ink-900">{data.id}</dd>
            <dt className="text-ink-500">{t('docviewer.source', { defaultValue: 'Source' })}</dt>
            <dd className="text-ink-900">{data.sourceKind}</dd>
            <dt className="text-ink-500">{t('docviewer.mime', { defaultValue: 'MIME' })}</dt>
            <dd className="font-mono text-xs text-ink-900">{data.mimeType || '—'}</dd>
            <dt className="text-ink-500">{t('docviewer.language', { defaultValue: 'Language' })}</dt>
            <dd className="text-ink-900">{data.language || '—'}</dd>
            <dt className="text-ink-500">{t('docviewer.status', { defaultValue: 'Status' })}</dt>
            <dd className="text-ink-900">{data.status}</dd>
            <dt className="text-ink-500">{t('docviewer.ocr', { defaultValue: 'OCR' })}</dt>
            <dd className="text-ink-900">{data.ocrKind || '—'}</dd>
            <dt className="text-ink-500">{t('docviewer.chunks', { defaultValue: 'Chunks' })}</dt>
            <dd className="font-mono text-ink-900">{data.chunkCount}</dd>
            <dt className="text-ink-500">{t('docviewer.size', { defaultValue: 'Size' })}</dt>
            <dd className="text-ink-900">{data.byteSize ? `${(data.byteSize / 1024).toFixed(1)} KB` : '—'}</dd>
            <dt className="text-ink-500">{t('docviewer.ingested', { defaultValue: 'Ingested' })}</dt>
            <dd className="text-ink-900">{data.ingestedAt ? <JpDate value={data.ingestedAt} /> : '—'}</dd>
            {data.error && (
              <>
                <dt className="text-ink-500">{t('docviewer.error', { defaultValue: 'Error' })}</dt>
                <dd className="text-warn-600">{data.error}</dd>
              </>
            )}
          </dl>
        )}
      </div>

      {data && <AccessPanel wsId={wsId!} docId={data.id} />}

      {data && (
        <div className="card-paper p-5 mt-4">
          <div className="text-[11px] uppercase tracking-[0.14em] text-ink-500 font-semibold mb-3">
            {t('docviewer.discussion', { defaultValue: 'Discussion' })}
          </div>
          <CommentThread workspaceId={wsId!} targetKind="document" targetId={data.id} />
        </div>
      )}

      {data && (
        <div className="card-paper p-5 mt-4">
          <div className="text-[11px] uppercase tracking-[0.14em] text-ink-500 font-semibold mb-3">
            {t('docviewer.similar', { defaultValue: 'Similar documents' })}
          </div>
          {similar.data && similar.data.length > 0 ? (
            <ul className="space-y-1.5">
              {similar.data.map((s) => (
                <li key={s.id} className="flex items-center justify-between text-sm">
                  <Link to={`/app/workspace/${wsId}/docs/${s.id}`} className="text-ink-900 hover:underline truncate">{s.title}</Link>
                  <span className="num text-[11px] text-ink-400 shrink-0">{s.score.toFixed(3)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[13px] text-ink-500">
              {t('docviewer.no_similar', { defaultValue: 'No similar documents yet. Upload more sources or run a search to explore the corpus.' })}{' '}
              <Link to={`/app/workspace/${wsId}/upload`} className="text-accent-600 hover:underline">{t('docviewer.upload_more', { defaultValue: 'Upload' })}</Link>
              {' · '}
              <Link to={`/app/workspace/${wsId}/search`} className="text-accent-600 hover:underline">{t('nav.search', { defaultValue: 'Search' })}</Link>
            </p>
          )}
        </div>
      )}
    </AppShell>
  );
}

function AccessPanel({ wsId, docId }: { wsId: string; docId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['doc-access', wsId, docId],
    queryFn: () => documentsApi.access(wsId, docId),
    enabled: open,
  });
  return (
    <div className="card-paper p-5 mt-4">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between text-left">
        <span className="text-[11px] uppercase tracking-[0.14em] text-ink-500 font-semibold">
          {t('access.title', { defaultValue: 'Who can find this' })}
        </span>
        <span className="text-ink-400">{open ? '▲' : '▾'}</span>
      </button>
      {open && (
        <div className="mt-3">
          {isLoading && <p className="text-[13px] text-ink-500">{t('common.loading', { defaultValue: 'Loading…' })}</p>}
          {data && (
            <>
              <p className="text-[13px] text-ink-600 mb-3">
                {t('access.summary', {
                  defaultValue: '{{allowed}} of {{total}} members can retrieve this document — enforced at query time.',
                  allowed: data.allowedCount,
                  total: data.totalMembers,
                })}
              </p>
              <ul className="space-y-1.5">
                {data.members.map((m) => (
                  <li key={m.userId} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-ink-900 truncate">
                      {m.name || m.email || m.userId}
                      <span className="ml-1.5 text-[10px] text-ink-400 uppercase tracking-wide">{m.role}</span>
                    </span>
                    <span className={`text-[12px] shrink-0 ${m.canAccess ? 'text-emerald-600' : 'text-ink-300'}`}>
                      {m.canAccess ? `✓ ${t('access.can', { defaultValue: 'can access' })}` : `✗ ${t('access.cannot', { defaultValue: 'no access' })}`}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
